import { getFileExtension } from '../../../lib/analytics';

const EXTEND_ARTIFACT_PREVIEW_PROVIDER = {
  id: 'extend',
  label: 'Extend',
  supportedExtensions: ['docx', 'pdf', 'txt', 'xlsx'],
} as const;

const DOCMENTIS_ARTIFACT_PREVIEW_PROVIDER = {
  id: 'docmentis',
  label: 'docMentis',
  supportedExtensions: ['pptx'],
} as const;

const NOVA_ARTIFACT_PREVIEW_PROVIDER = {
  id: 'nova',
  label: 'Nova',
  supportedExtensions: ['html', 'htm'],
} as const;

const ARTIFACT_PREVIEW_PROVIDERS = [
  EXTEND_ARTIFACT_PREVIEW_PROVIDER,
  DOCMENTIS_ARTIFACT_PREVIEW_PROVIDER,
  NOVA_ARTIFACT_PREVIEW_PROVIDER,
] as const;

const ARTIFACT_PREVIEW_EXTENSION_ALIASES = {
  htm: 'html',
} as const;

export type ArtifactPreviewProviderId = (typeof ARTIFACT_PREVIEW_PROVIDERS)[number]['id'];

type ArtifactPreviewExtension = (typeof ARTIFACT_PREVIEW_PROVIDERS)[number]['supportedExtensions'][number];

export type ArtifactPreviewFileType = Exclude<
  ArtifactPreviewExtension,
  keyof typeof ARTIFACT_PREVIEW_EXTENSION_ALIASES
>;

export interface ArtifactPreview {
  fileType: ArtifactPreviewFileType;
  fileUrl: string;
  fileUuid: string;
  providerLabel: string;
  sourceUrl: string;
  title: string;
}

export interface ArtifactPreviewMenuItem {
  fileType: ArtifactPreviewFileType;
  filename: string;
  id: string;
  messageUuid?: string;
  providerId: ArtifactPreviewProviderId;
  providerLabel: string;
  source: string;
  uuid: string;
}

export function getArtifactPreviewFileType(filename: string): ArtifactPreviewFileType | undefined {
  const extension = getFileExtension(filename);
  if (!extension) return undefined;

  const supportedExtensions = ARTIFACT_PREVIEW_PROVIDERS.flatMap((provider) => provider.supportedExtensions);

  if (!(supportedExtensions as readonly string[]).includes(extension)) return undefined;

  return (
    ARTIFACT_PREVIEW_EXTENSION_ALIASES[extension as keyof typeof ARTIFACT_PREVIEW_EXTENSION_ALIASES] ??
    (extension as ArtifactPreviewFileType)
  );
}

function getArtifactPreviewProvider(fileType: ArtifactPreviewFileType) {
  return ARTIFACT_PREVIEW_PROVIDERS.find((candidate) =>
    (candidate.supportedExtensions as readonly string[]).includes(fileType),
  );
}

export function getArtifactPreviewProviderLabel(fileType: ArtifactPreviewFileType): string {
  const provider = getArtifactPreviewProvider(fileType);

  return provider?.label ?? EXTEND_ARTIFACT_PREVIEW_PROVIDER.label;
}

export function getArtifactPreviewProviderId(fileType: ArtifactPreviewFileType): ArtifactPreviewProviderId {
  const provider = getArtifactPreviewProvider(fileType);

  return provider?.id ?? EXTEND_ARTIFACT_PREVIEW_PROVIDER.id;
}
